import React, { useState } from 'react';
import { useInfluencerStore } from '@/stores/influencerStore';
import { PersonalDashboard } from './PersonalDashboard';
import { ReferralLinkGenerator } from './ReferralLinkGenerator';
import { EarningsCalculator } from './EarningsCalculator';
import { PaymentHistory } from './PaymentHistory';
import { PerformanceAnalytics } from './PerformanceAnalytics';
import { SocialMediaToolkit } from './SocialMediaToolkit';
import { TierProgress } from './TierProgress';
import { ReferralStats } from './ReferralStats';
import { EmailMarketing } from './EmailMarketing';
import { Tab } from '@headlessui/react';
import { 
  BarChart3, 
  Link, 
  DollarSign, 
  CreditCard, 
  TrendingUp, 
  Share2, 
  Award,
  Users,
  Mail
} from 'lucide-react';

export const InfluencerView: React.FC = () => {
  const { dashboardData, loading } = useInfluencerStore();
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    { name: 'Dashboard', icon: BarChart3, component: PersonalDashboard },
    { name: 'Referral Link', icon: Link, component: ReferralLinkGenerator },
    { name: 'Earnings', icon: DollarSign, component: EarningsCalculator },
    { name: 'Payments', icon: CreditCard, component: PaymentHistory },
    { name: 'Analytics', icon: TrendingUp, component: PerformanceAnalytics },
    { name: 'Social Media', icon: Share2, component: SocialMediaToolkit },
    { name: 'Tier Progress', icon: Award, component: TierProgress },
    { name: 'Referral Stats', icon: Users, component: ReferralStats },
    { name: 'Email Marketing', icon: Mail, component: EmailMarketing },
  ];

  if (loading.dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Influencer Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track your referrals, earnings, and performance metrics.
        </p>
      </div>

      {/* Quick Stats Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-blue-500 text-white">
                    <Users className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Referrals</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {dashboardData.personalStats.totalSignups.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-green-500 text-white">
                    <DollarSign className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Earnings</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      ${dashboardData.personalStats.totalEarnings.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-purple-500 text-white">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Conversion Rate</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {dashboardData.personalStats.conversionRate.toFixed(1)}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-yellow-500 text-white">
                    <Award className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Current Tier</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      Tier {dashboardData.tier.current}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabbed Interface */}
      <div className="bg-white shadow rounded-lg">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-1 border-b border-gray-200 px-6">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.name}
                className={({ selected }) =>
                  `flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    selected
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="p-6">
            {tabs.map((tab, index) => (
              <Tab.Panel key={tab.name}>
                <tab.component />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
}; 